import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights } from '../constants/theme';

const BreadcrumbContainer = styled.nav`
  display: flex;
  align-items: center;
  padding: ${Spacing.md}px 0;
  margin-bottom: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.borderLight};
`;

const BreadcrumbList = styled.ol`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: ${Spacing.sm}px;
`;

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
`;

const BreadcrumbLink = styled(Link)`
  color: ${Colors.textSecondary};
  text-decoration: none;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.normal};
  transition: color 200ms ease;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: 4px;
  
  &:hover {
    color: ${Colors.primary};
    background: ${Colors.surfaceHover};
  }
  
  &:focus {
    outline: 2px solid ${Colors.primary};
    outline-offset: 2px;
    color: ${Colors.primary};
  }
`;

const CurrentPage = styled.span`
  color: ${Colors.textPrimary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  padding: ${Spacing.xs}px ${Spacing.sm}px;
`;

const BreadcrumbSeparator = styled.span`
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.sm}px;
  user-select: none;
`;

interface BreadcrumbItem {
  label: string;
  path?: string;
  current?: boolean;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  showHome?: boolean;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, showHome = true }) => {
  const location = useLocation();
  
  // Auto-generate breadcrumbs based on current route if no items provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const breadcrumbs: BreadcrumbItem[] = [];
    
    if (showHome) {
      breadcrumbs.push({ label: 'Dashboard', path: '/' });
    }
    
    pathSegments.forEach((segment, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      const isLast = index === pathSegments.length - 1;
      
      let label = segment.charAt(0).toUpperCase() + segment.slice(1);
      
      // Convert common route names to user-friendly labels
      switch (segment) {
        case 'insights':
          label = 'AI Insights';
          break;
        case 'profiles':
          label = 'Employee Profiles';
          break;
        case 'data-sources':
          label = 'Data Sources';
          break;
        default:
          label = label.replace(/-/g, ' ');
      }
      
      breadcrumbs.push({
        label,
        path: isLast ? undefined : path,
        current: isLast
      });
    });
    
    return breadcrumbs;
  };
  
  const breadcrumbItems = items || generateBreadcrumbs();
  
  if (breadcrumbItems.length <= 1) {
    return null; // Don't show breadcrumbs for single-level navigation
  }
  
  return (
    <BreadcrumbContainer 
      aria-label="Breadcrumb navigation"
      role="navigation"
    >
      <BreadcrumbList>
        {breadcrumbItems.map((item, index) => (
          <BreadcrumbItem key={index}>
            {index > 0 && (
              <BreadcrumbSeparator aria-hidden="true">
                /
              </BreadcrumbSeparator>
            )}
            
            {item.path && !item.current ? (
              <BreadcrumbLink 
                to={item.path}
                aria-label={`Navigate to ${item.label}`}
              >
                {item.label}
              </BreadcrumbLink>
            ) : (
              <CurrentPage 
                aria-current="page"
                aria-label={`Current page: ${item.label}`}
              >
                {item.label}
              </CurrentPage>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </BreadcrumbContainer>
  );
};

export default Breadcrumbs;